import React from "react";
import { useForm, useWatch } from "react-hook-form";
import useFormActions from "../useFormActions";
import StepWrapper from "./StepWrapper";
import { TextField } from "../../common/forms/components/TextField";
import { PhoneField } from "../../common/forms/components/PhoneField";
import { DropdownField } from "../../common/forms/components/DropdownField";
import { COUNTRY_KEY, TIME_ZONE_KEY, timeFormats, USER_FUNCTION_KEY } from "../../common/forms/constants";
import CheckboxField from "../../common/forms/components/CheckboxField";
import SegmentedRadioFieldset from "../../common/forms/components/SegmentedRadioFieldset";
import { checkboxIsRequired } from "../../common/forms/utils";
import { subscriptionStepProps } from "../../common/types/subscription";

export default function Step5({
	formStep,
	subtitle,
	title,
}: subscriptionStepProps) {
	const { onUpdateField, onUpdateForm, onSubmitForm } = useFormActions();
	const { control, handleSubmit } = useForm({
		defaultValues: {
			timeFormat: timeFormats[0].key,
			newsletter: "0",
		},
	});

	const newsletterKey = "newsletter";
	const newsletterInput = useWatch({
		control,
		name: newsletterKey,
	});
	const newsletterIsEnabled = newsletterInput === "1";

	return (
		<StepWrapper
			formStep={formStep}
			onSubmit={handleSubmit((data) => {
				onUpdateForm(data);
				onSubmitForm();
			})}
			title={title}
			subtitle={subtitle}
		>
			<div className="grid justify-space-between">
				<fieldset className="form-section form-section-vertical">
					<legend className="form-section-title">
						Personal Information
					</legend>
					<TextField
						autoComplete="given-name"
						control={control}
						label="First Name"
						id="firstName"
						name="firstName"
						onInputChange={onUpdateField}
						required
					/>
					<TextField
						autoComplete="family-name"
						control={control}
						label="Last Name"
						id="lastName"
						name="lastName"
						onInputChange={onUpdateField}
						required
					/>
					<PhoneField
						control={control}
						label="Phone Number"
						id="phone"
						name="phoneNumber"
						onInputChange={onUpdateField}
					/>
					<DropdownField
						choicesEndpoint="user_function"
						control={control}
						dropdownToggle="Please select your job function"
						label="Job Function"
						name={USER_FUNCTION_KEY}
						onFieldChange={onUpdateField}
						required
					/>
				</fieldset>
				<fieldset className="form-section form-section-vertical">
					<legend className="form-section-title">
						Company Information
					</legend>
					<TextField
						autoComplete="organization"
						control={control}
						label="Company Name"
						id="company"
						name="companyName"
						onInputChange={onUpdateField}
					/>
					<TextField
						autoComplete="street-address"
						control={control}
						label="Address 1"
						id="address1"
						name="address1"
						onInputChange={onUpdateField}
					/>
					<TextField
						control={control}
						label="Address 2"
						id="address2"
						name="address2"
						onInputChange={onUpdateField}
					/>
					<DropdownField
						choicesEndpoint="country-id"
						control={control}
						dropdownToggle="Please select your country"
						label="Country"
						name={COUNTRY_KEY}
						onFieldChange={onUpdateField}
						required
					/>
					<div className="zip-code form-group">
						<TextField
							autoComplete="address-level2"
							control={control}
							id="city"
							label="City"
							name="city"
							onInputChange={onUpdateField}
						/>
						<TextField
							autoComplete="postal-code"
							control={control}
							label="ZIP Code"
							id="zipCode"
							name="zipCode"
							onInputChange={onUpdateField}
						/>
					</div>
				</fieldset>
				<CheckboxField
					control={control}
					name={newsletterKey}
					onInputChange={onUpdateField}
					label="I would like to receive the daily newsletters from ch-aviation with all of ch-aviation’s news articles on the industry"
					required
					validate={{
						required: checkboxIsRequired,
					}}
				/>
				{newsletterIsEnabled && (
					<div className="newsletter-settings flex gap-20">
						<div className="grid__col-12 grid__col-md-6">
							<SegmentedRadioFieldset
								criterionKey="timeFormat"
								control={control}
								label="Time format"
								radioDefinition={timeFormats}
								required={newsletterIsEnabled}
							/>
						</div>
						<div className="grid__col-12 grid__col-md-6">
							<DropdownField
								additionalClasses="dropdown--secondary"
								choicesEndpoint="timezone"
								control={control}
								dropdownToggle="Please select your time zone"
								label="Time zone"
								name={TIME_ZONE_KEY}
								onFieldChange={onUpdateField}
								required={newsletterIsEnabled}
							/>
						</div>
					</div>
				)}
			</div>
		</StepWrapper>
	);
}
